import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import { map, Subject, take, takeUntil } from "rxjs";
import { RouterModule } from '@angular/router';
import { DashboardHeaderComponent } from "@app/layout/components/dashboard-header/dashboard-header.component";
import { DashboardType } from '@app/core/enums/dashboard-types.enum';
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { WorkCompUser } from '@app/core/interfaces/user.interface';
import { us_states } from '@app/shared/interfaces/us_states';
import { WorkCompAccountSettingsService } from '@app/features/login/services/work-comp-account-settings.service';
import { ToastService } from '@app/core/services/toast.service';
import { UnsavedChangesGuard } from '@app/shared/guards/unsaved-changes.guard';
import {
  ChangePasswordModalComponent
} from "@app/shared/interfaces/change-password-modal/change-password-modal.component";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {ContactUsInfoComponent} from "@app/shared/components/contact-us-info/contact-us-info.component";
import {SharedModule} from "@app/shared/shared.module";
import {SaveResult} from "@app/shared/interfaces/saveResult.interface";
import {
  AccountPersonalInfoFormComponent
} from '@app/shared/components/account-personal-info-form/account-personal-info-form.component';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-work-comp-account-preferences',
  imports: [
    RouterModule,
    DashboardHeaderComponent,
    MatCardModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogModule,
    ContactUsInfoComponent,
    SharedModule,
    AccountPersonalInfoFormComponent
],
  templateUrl: './work-comp-account-preferences.component.html',
  styleUrl: './work-comp-account-preferences.component.scss'
})
export class WorkCompAccountPreferencesComponent implements OnInit, OnDestroy, UnsavedChangesGuard {
  ngDestroy$ = new Subject();
  userData!: WorkCompUser;
  dashboardType = DashboardType.WorkComp;

  accountForm!: FormGroup;
  companyInfoForm!: FormGroup;

  initialAccountFormValues: any;
  initalCompanyInfoFormValues: any;

  states = us_states;

  constructor(
    private fb: FormBuilder,
    private workCompAccountSettingsService: WorkCompAccountSettingsService,
    private toastService: ToastService,
    private dialog: MatDialog,
  ) {}

  canDeactivate(): boolean | import('rxjs').Observable<boolean> {
    if (!this.accountForm.dirty && !this.companyInfoForm.dirty) {
      return true;
    }

    let confirmed = false;
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => confirmed = true);
    return dialog.afterClosed().pipe(map(() => confirmed));
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.accountForm?.dirty || this.companyInfoForm.dirty) {
      event.preventDefault();
      event.returnValue = ''; // required for modern browsers
      return '';
    }
    return undefined;
  }


  ngOnInit(): void {
    this.accountForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      phone: [ '', [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]],
      faxNumber: ['', [Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]],
      address1: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      postalCode: ['',
        [
          Validators.required,
          Validators.pattern(/^\d{5}$/)
        ]
      ],
    });

    this.initialAccountFormValues = this.accountForm.getRawValue();

    this.companyInfoForm = this.fb.group({
      managerFirstName: ['', Validators.required],
      managerLastName: ['', Validators.required],
      managerEmail: ['', [Validators.required, Validators.email]],
      managerCompanyName: ['', [Validators.required]],
      managerContactFaxNumber: ['', [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]],
      managerChartNotesFaxNumber: ['', [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]],
    });

    this.initalCompanyInfoFormValues = this.accountForm.getRawValue();

    this.workCompAccountSettingsService.getWorkCompAccountSettings$()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((user: WorkCompUser | null) => {
        if (user) {
          this.userData = user;

          this.accountForm.patchValue({
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            phone: user.phoneNumber ?? '',
            faxNumber: user.faxNumber ?? '',
            email: user.email ?? '',
            address1: user.address ?? '',
            city: user.city ?? '',
            state: user.state ?? '',
            postalCode: user.postalCode ?? '',
          });

          this.initialAccountFormValues = this.accountForm.getRawValue();

          this.companyInfoForm.patchValue({
            managerFirstName: user.managerFirstName ?? '',
            managerLastName: user.managerLastName ?? '',
            managerEmail: user.managerEmail ?? '',
            managerContactFaxNumber: user.faxForInformation ?? '',
            managerChartNotesFaxNumber: user.faxForChartNote ?? '',
            managerCompanyName: user.companyName ?? ''
          });

          this.initalCompanyInfoFormValues = this.companyInfoForm.getRawValue();
        }
      });

    this.listenToControlChanges(this.accountForm, 'phone');
    this.listenToControlChanges(this.accountForm, 'faxNumber');
    this.listenToControlChanges(this.companyInfoForm, 'managerContactFaxNumber');
    this.listenToControlChanges(this.companyInfoForm, 'managerChartNotesFaxNumber');
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
  }

  listenToControlChanges(formGroup: FormGroup, controlName: string): void {
    formGroup.get(controlName)?.valueChanges
    .pipe(takeUntil(this.ngDestroy$))
    .subscribe(value => {
      if (value) {
        const formattedValue = this.formatPhoneNumber(value);
        formGroup.get(controlName)?.setValue(formattedValue, { emitEvent: false });
      }
    });
  }

  formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/\D/g, '');

  if (cleaned.length <= 3) {
    return `(${cleaned}`;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.substr(0, 3)}) ${cleaned.substr(3)}`;
  } else {
    return `(${cleaned.substr(0, 3)}) ${cleaned.substr(3, 3)}-${cleaned.substr(6, 4)}`;
  }
}

openChangePasswordModal() {
    this.dialog.open(ChangePasswordModalComponent, {});
}

  onPreferencesFormSubmit(): void {
    if (this.accountForm.valid) {

      const updatedUser: WorkCompUser = {
        ...this.userData,
        firstName: this.accountForm.value.firstName,
        lastName: this.accountForm.value.lastName,
        phoneNumber: this.accountForm.value.phone,
        faxNumber: this.accountForm.value.faxNumber,
        address: this.accountForm.value.address1,
        city: this.accountForm.value.city,
        state: this.accountForm.value.state,
        postalCode: this.accountForm.value.postalCode
      };

      this.workCompAccountSettingsService.updateWorkCompAccountSettings$(updatedUser)
        .subscribe((response: SaveResult) => {
          console.log(response);
          if (response.errorMessages?.length > 0) {
            this.toastService.showErrorToast('Something went wrong. Please try again.');
          } else {
            this.initialAccountFormValues = this.accountForm.getRawValue();
            this.accountForm.markAsPristine();
            this.toastService.showSuccessToast('Account settings updated.');
          }
        });
    }
  }

  onCompanyInfoFormSubmit(): void {
    if (this.companyInfoForm.valid) {

      const updatedUser: WorkCompUser = {
        ...this.userData,
        managerFirstName: this.companyInfoForm.value.managerFirstName,
        managerLastName: this.companyInfoForm.value.managerLastName,
        managerEmail: this.companyInfoForm.value.managerEmail,
        faxForInformation: this.companyInfoForm.value.managerContactFaxNumber,
        faxForChartNote: this.companyInfoForm.value.managerChartNotesFaxNumber,
        companyName: this.companyInfoForm.value.managerCompanyName
      };


      this.workCompAccountSettingsService.updateWorkCompAccountSettings$(updatedUser)
        .subscribe((response: SaveResult) => {
          if (response.errorMessages?.length > 0) {
            this.toastService.showErrorToast('Something went wrong. Please try again.');
          } else {
            this.initalCompanyInfoFormValues = this.companyInfoForm.getRawValue();
            this.companyInfoForm.markAsPristine();
            this.toastService.showSuccessToast('Account settings updated.');
          }
        });
      }
    }

    onPreferencesCancel() {
      const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
      dialog.componentInstance.title = 'Unsaved Changes';
      dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to cancel?';
      dialog.componentInstance.onConfirm
        .pipe(take(1))
        .subscribe(() => this.accountForm.reset(this.initialAccountFormValues));
    }

  onCompanyInfoCancel() {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to cancel?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => this.companyInfoForm.reset(this.initalCompanyInfoFormValues));
  }
}
